import React from 'react';
import { Link } from 'react-router-dom';
import usePageTitle from '../hooks/usePageTitle';
import '../styles/pages/AboutUsPage.css';

const AboutUsPage = () => {
  usePageTitle('About Us', 'Meet Michele of The Cookied Baker and the story behind The Icing Calculator.');
  return (
    <div className="about-page">

      <div className="return-link-container return-link-top">
        <Link to="/" className="return-link">← Return to The Icing Calculator</Link>
      </div>

      {/* Hero */}
      <div className="about-hero">
        <div className="about-hero-icon">
          <img src={`${process.env.PUBLIC_URL}/images/michele-portrait.jpg`} alt="Michele, founder of The Icing Calculator" className="about-hero-photo" />
        </div>
        <p className="about-hero-sub">
          Hi, I'm Michele.<br />Cookie decorator. Small business owner.<br />And the reason this calculator exists.
        </p>
      </div>

      {/* Story */}
      <div className="about-card">
        <h2 className="about-section-title">The Story</h2>
        <p>
          Like so many decorators, I fell in love with the <em>art</em> of cookies: mixing colors,
          piping details, bringing designs to life. But there was always one part of the process I
          dreaded: the icing math.
        </p>
        <p>
          Every order meant pulling out a spreadsheet (with formulas I barely understood) or
          scribbling numbers on scraps of paper, trying to guess how much royal icing I needed.
          Making too much meant wasted icing, while making too little meant added stress.
          It took up precious time I could've spent actually decorating.
        </p>
        <p>
          That's when my husband, who loves solving problems, stepped in. He saw how much time
          and frustration went into icing planning, so he helped me build <strong>The Icing Calculator</strong> to
          make it simple. No more spreadsheets, guesswork, or frustrations. Just a clean, easy-to-use tool
          that estimates how much icing I need for any project.
        </p>
        <p>
          We made it to solve my own icing-planning headaches. Then we realized other cookiers could use it too.
          So we shared it.
        </p>
      </div>

      {/* My Cookie Business */}
      <div className="about-card">
        <div className="about-bakery-inner">
          <div className="about-bakery-text">
            <h2 className="about-section-title">Meet The Cookied Baker</h2>
            <p>I'm Michele, the baker behind <strong>The Cookied Baker</strong>, based in Sugar Land, Texas.</p>
            <p>
              I create thoughtfully designed sugar cookies for celebrations, corporate events, logo cookies, hands-on classes, and live cookie experiences.
              Every cookie is baked with love and decorated by hand, with just the right amount of icing.
            </p>
            <p>
              Want to see what I'm working on? Come find me online. Let's Get COOKIED!
            </p>
            <a
              href="https://www.thecookiedbaker.com"
              target="_blank"
              rel="noopener noreferrer"
              className="about-bakery-btn"
            >
              Visit TheCookiedBaker.com
            </a>
          </div>
          <div className="about-bakery-logo">
            <a href="https://www.thecookiedbaker.com" target="_blank" rel="noopener noreferrer">
              <img
                src={`${process.env.PUBLIC_URL}/images/CB Logo wSlogan.png`}
                alt="The Cookied Baker logo"
                className="about-bakery-logo-img"
              />
            </a>
          </div>
        </div>
      </div>



      {/* Social Media */}
      <div className="about-card about-social-card">
        <h2 className="about-section-title">Find Me Online</h2>
        <div className="about-social-links">
          <a href="https://www.instagram.com/thecookiedbaker?igsh=MWY1dGxsaTZmdGV6NA%3D%3D&utm_source=qr" target="_blank" rel="noopener noreferrer" className="about-social-link">
            <svg className="about-social-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 1.366.062 2.633.334 3.608 1.308.975.975 1.246 2.242 1.308 3.608.058 1.266.07 1.646.07 4.85s-.012 3.584-.07 4.85c-.062 1.366-.334 2.633-1.308 3.608-.975.975-2.242 1.246-3.608 1.308-1.266.058-1.646.07-4.85.07s-3.584-.012-4.85-.07c-1.366-.062-2.633-.334-3.608-1.308-.975-.975-1.246-2.242-1.308-3.608C2.175 15.584 2.163 15.204 2.163 12s.012-3.584.07-4.85c.062-1.366.334-2.633 1.308-3.608.975-.975 2.242-1.246 3.608-1.308C8.416 2.175 8.796 2.163 12 2.163zm0-2.163C8.741 0 8.332.014 7.052.072 5.197.157 3.355.673 2.014 2.014.673 3.355.157 5.197.072 7.052.014 8.332 0 8.741 0 12c0 3.259.014 3.668.072 4.948.085 1.855.601 3.697 1.942 5.038 1.341 1.341 3.183 1.857 5.038 1.942C8.332 23.986 8.741 24 12 24s3.668-.014 4.948-.072c1.855-.085 3.697-.601 5.038-1.942 1.341-1.341 1.857-3.183 1.942-5.038.058-1.28.072-1.689.072-4.948s-.014-3.668-.072-4.948c-.085-1.855-.601-3.697-1.942-5.038C20.645.673 18.803.157 16.948.072 15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zm0 10.162a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z"/>
            </svg>
            <span>Instagram</span>
          </a>
          <a href="https://www.facebook.com/share/1H6YBs5Djk/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" className="about-social-link">
            <svg className="about-social-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.792-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.97h-1.513c-1.491 0-1.956.93-1.956 1.874v2.25h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z"/>
            </svg>
            <span>Facebook</span>
          </a>
          <a href="https://www.tiktok.com/@thecookiedbaker?_r=1&_t=ZT-97NdwCOLzNl" target="_blank" rel="noopener noreferrer" className="about-social-link">
            <svg className="about-social-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.75a4.85 4.85 0 0 1-1.01-.06z"/>
            </svg>
            <span>TikTok</span>
          </a>
          <a href="https://thecookiedbaker.com/" target="_blank" rel="noopener noreferrer" className="about-social-link">
            <svg className="about-social-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="10"/>
              <line x1="2" y1="12" x2="22" y2="12"/>
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
            </svg>
            <span>TheCookiedBaker.com</span>
          </a>
        </div>
      </div>

      <div className="return-link-container return-link-bottom">
        <Link to="/" className="return-link">← Return to The Icing Calculator</Link>
      </div>

    </div>
  );
};

export default AboutUsPage;
